import a11yIIBScreenReaderService, {
    A11yForIIBScreenReaderIteration,
} from '../../../services/a11yIIBScreenReaderService';

/**
 * Maps common superscript text content to human-readable screen reader labels.
 * @param {string} text - The text content of the <sup> element.
 * @returns {string} A screen-reader-friendly description.
 */
function getSuperscriptLabel(text) {
    const trimmed = text.trim();
    if (trimmed === '2') return 'squared';
    if (trimmed === '3') return 'cubed';
    return `to the power of ${trimmed}`;
}

/**
 * Maps common subscript text content to human-readable screen reader labels.
 * @param {string} text - The text content of the <sub> element.
 * @returns {string} A screen-reader-friendly description.
 */
function getSubscriptLabel(text) {
    return `sub ${text.trim()}`;
}

/**
 * Checks whether a superscript's text is a common English ordinal suffix (1st, 2nd, 3rd, 4th...).
 * Native <sup> is also valid markup for ordinals and other non-mathematical typography, so those
 * must be left untouched rather than announced as an exponent.
 * @param {string} text - The text content of the <sup> element.
 * @returns {boolean}
 */
function isOrdinalSuffix(text) {
    return /^(st|nd|rd|th)$/i.test(text.trim());
}

/**
 * Inserts a visually-hidden sibling span carrying the spoken label right after the given element,
 * then hides the original (visual-only) element from assistive technology.
 *
 * <sup>/<sub> have an implicit ARIA role ("superscript"/"subscript") whose accessible name is
 * prohibited by the ARIA spec, so aria-label on those elements is invalid and may be ignored by
 * assistive technology. Using a plain sibling element for the label avoids relying on that
 * prohibited-naming role, while aria-hidden on the original element keeps the visual glyph from
 * being announced a second time.
 *
 * @param {Element} element - The <sup>/<sub> element being patched.
 * @param {string} label - The screen-reader-friendly label to announce.
 */
function insertHiddenLabel(element, label) {
    const hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'sr-only';
    hiddenLabel.textContent = label;
    element.parentNode.insertBefore(hiddenLabel, element.nextSibling);
    element.setAttribute('aria-hidden', 'true');
}

/**
 * Patches <sup> and <sub> elements within the given wrapper for screen reader accessibility.
 * Inserts visually-hidden text after superscript/subscript elements so screen readers
 * announce them meaningfully (e.g., "x squared" instead of "x 2").
 *
 * Only applies when a11y IIB screen reader support is enabled.
 * Skips elements already inside MathJax containers (which have their own accessibility handling)
 * and superscripts that are ordinal suffixes rather than exponents.
 *
 * @param {Element} wrapperElement - The DOM element containing question content.
 */
function patchSuperscriptAccessibility(wrapperElement) {
    if (!a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v7)) {
        return;
    }

    if (!wrapperElement) {
        return;
    }

    const supElements = wrapperElement.querySelectorAll('sup');
    supElements.forEach((sup) => {
        if (sup.getAttribute('data-a11y-patched')) {
            return;
        }
        if (isInsideMathJaxContainer(sup)) {
            return;
        }
        const text = sup.textContent;
        if (!text || !text.trim() || isOrdinalSuffix(text)) {
            return;
        }
        sup.setAttribute('data-a11y-patched', 'true');
        insertHiddenLabel(sup, getSuperscriptLabel(text));
    });

    const subElements = wrapperElement.querySelectorAll('sub');
    subElements.forEach((sub) => {
        if (sub.getAttribute('data-a11y-patched')) {
            return;
        }
        if (isInsideMathJaxContainer(sub)) {
            return;
        }
        const text = sub.textContent;
        if (!text || !text.trim()) {
            return;
        }
        sub.setAttribute('data-a11y-patched', 'true');
        insertHiddenLabel(sub, getSubscriptLabel(text));
    });
}

/**
 * Checks if an element is inside a MathJax container (which has its own accessibility handling).
 * @param {Element} element
 * @returns {boolean}
 */
function isInsideMathJaxContainer(element) {
    let current = element.parentNode;
    while (current && current !== document) {
        if (current.nodeType === Node.ELEMENT_NODE) {
            const tag = current.tagName ? current.tagName.toLowerCase() : '';
            if (tag === 'mjx-container') return true;
            const className = current.className || '';
            if (className.indexOf('MathJax') !== -1 || className.indexOf('math-tex') !== -1) return true;
        }
        current = current.parentNode;
    }
    return false;
}

export { getSuperscriptLabel, getSubscriptLabel, isInsideMathJaxContainer, isOrdinalSuffix };
export default patchSuperscriptAccessibility;
